/**
 * GoalRegenerateChip — the "Regenerate with a different framework" quick-
 * swap chip that shows on every post card (Prompt Posts, Researched
 * Posts, POV). Fetches the goal catalog once (session-cached), pops a
 * lightweight goal picker, and calls `POST /analyze/{id}/posts/regenerate-
 * one` when the user chooses. Replaces just that one post in the parent
 * list via the `onReplaced(newPost)` callback — keeps the rest of the
 * batch untouched.
 */
import React, { useEffect, useState } from 'react';
import { Sparkles, Loader2, Check, ChevronDown } from 'lucide-react';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '../ui/popover';
import { toast } from 'sonner';
import { api, linkedge } from '../../lib/api';
import { getErrorMessage } from '../../lib/errors';

let _goalCatalogPromise = null;
function loadGoalCatalogOnce() {
  if (!_goalCatalogPromise) {
    _goalCatalogPromise = api.get('/posts/goals').then((r) => r?.data?.goals || []).catch(() => []);
  }
  return _goalCatalogPromise;
}

export default function GoalRegenerateChip({
  analysisId,
  source,          // 'prompt' | 'researched' | 'pov'
  index,           // number for prompt/researched, undefined for pov
  currentGoal,     // string | null — the goal used to write this post
  onReplaced,      // (newPost) => void  — parent updates its list
  tones,           // string[]  — pass current tones so the rewrite keeps the same voice controls
  compact = false,
}) {
  const [open, setOpen] = useState(false);
  const [goals, setGoals] = useState([]);
  const [busyKey, setBusyKey] = useState(null);

  useEffect(() => {
    let alive = true;
    loadGoalCatalogOnce().then((catalog) => {
      if (alive) setGoals(catalog);
    });
    return () => { alive = false; };
  }, []);

  const trigger = async (goalKey) => {
    if (busyKey) return;
    setBusyKey(goalKey);
    try {
      const payload = { source, goal: goalKey };
      if (source !== 'pov') payload.index = index;
      if (tones && tones.length) payload.tones = tones;
      const data = await linkedge.regenerateOnePost(analysisId, payload);
      const newPost = data?.post || data?.pov || null;
      if (!newPost) throw new Error('No post returned');
      const goalLabel = goals.find((g) => g.key === goalKey)?.framework_name || goalKey;
      toast.success(`Rewritten with ${goalLabel}.`);
      onReplaced?.(newPost);
      setOpen(false);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Regeneration failed'));
    } finally {
      setBusyKey(null);
    }
  };

  const btnCls = compact
    ? 'inline-flex items-center gap-1 rounded-md px-2 py-1 text-[10.5px] font-mono-edge tracking-[0.12em] uppercase border border-border bg-background text-muted-foreground hover:text-foreground hover:border-foreground/30 transition'
    : 'inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-[11.5px] font-semibold border border-border bg-background text-muted-foreground hover:text-foreground hover:border-foreground/30 transition';

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={btnCls}
          data-testid={`goal-regenerate-trigger-${source}${index !== undefined ? `-${index}` : ''}`}
          title="Regenerate this post with a different framework"
        >
          <Sparkles size={11} />
          {compact ? 'Framework' : 'Try another framework'}
          <ChevronDown size={11} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[300px] p-1.5" data-testid="goal-regenerate-popover">
        <div className="px-2 pt-1.5 pb-1.5 border-b border-border">
          <p className="text-[10.5px] font-mono-edge tracking-[0.16em] uppercase text-muted-foreground">Rewrite with framework</p>
          <p className="text-[11px] text-muted-foreground mt-0.5">Same topic — different structure.</p>
        </div>
        <div className="py-1 max-h-[320px] overflow-y-auto">
          {goals.length === 0 && (
            <p className="px-2 py-2 text-[12px] text-muted-foreground">Loading frameworks…</p>
          )}
          {goals.map((g) => {
            const active = currentGoal === g.key;
            const busy = busyKey === g.key;
            return (
              <button
                key={g.key}
                type="button"
                onClick={() => trigger(g.key)}
                disabled={busyKey !== null}
                className={`w-full text-left rounded-md px-2 py-1.5 flex items-start gap-2 transition ${
                  active ? 'bg-primary/10' : 'hover:bg-muted'
                } disabled:opacity-60`}
                data-testid={`goal-regenerate-option-${g.key}`}
              >
                <span className="mt-0.5 shrink-0">
                  {busy ? <Loader2 size={12} className="animate-spin text-primary" />
                    : active ? <Check size={12} className="text-primary" />
                    : <Sparkles size={11} className="text-muted-foreground" />}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-[12.5px] font-semibold text-foreground truncate">{g.label}</span>
                  <span className="block text-[10.5px] text-muted-foreground truncate">{g.framework_name}</span>
                </span>
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
